import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useAddCareTeamMember,
  useRemoveCareTeamMember,
  useSetPrimaryClinician,
  useUpdateCareTeamMemberRole,
} from '@/features/patient-record/hooks/useCareTeamMutations';
import { useDirtyDialogGuard } from '@/features/patient-record/hooks/useDirtyDialogGuard';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import type { CareTeamMemberDetailViewModel } from '@/models/patient';

import { AddMemberForm } from './AddMemberForm';
import { EditorFooter } from './EditorFooter';
import { Field } from './Field';
import { MemberList } from './MemberList';
import { MetadataFields } from './MetadataFields';
import { PrimarySection } from './PrimarySection';
import { isForbidden, type ResolvedMemberName } from './utils';

interface CareTeamEditorProps {
  readonly guid: string;
  readonly members: readonly CareTeamMemberDetailViewModel[];
  readonly resolveName: (m: CareTeamMemberDetailViewModel) => ResolvedMemberName;
  readonly initialProgram: string | null;
  readonly onClose: () => void;
}

/** Client temp-id prefix for members added in the current edit session but not
 *  yet persisted. The save diff keys off `userId` (not this id), so the prefix
 *  only needs to yield a stable, collision-free React key + remove handle. */
const NEW_MEMBER_ID_PREFIX = 'new:';

/** The whole editable working copy: the team roster plus the program metadata. */
interface CareTeamDraft {
  readonly members: readonly CareTeamMemberDetailViewModel[];
  readonly program: string;
}

/** Normalize the program value the way both the dirty-check and the commit do. */
const normalizeProgram = (program: string): string | null =>
  program.trim().length > 0 ? program : null;

/**
 * Members are compared order-insensitively, matching by `userId`: equal unless a
 * member was added, removed, or had its role / isPrimary changed. Picker
 * selection state lives in AddMemberForm and never feeds this computation.
 */
const membersEqual = (
  a: readonly CareTeamMemberDetailViewModel[],
  b: readonly CareTeamMemberDetailViewModel[],
): boolean => {
  if (a.length !== b.length) return false;
  const byUser = new Map(a.map((m) => [m.userId, m] as const));
  for (const w of b) {
    const orig = byUser.get(w.userId);
    if (orig === undefined) return false; // added (or swapped for a removed one)
    if (w.role !== orig.role || w.isPrimary !== orig.isPrimary) return false;
  }
  return true;
};

/** Draft equality = same normalized program AND order-insensitive member match. */
const draftsEqual = (a: CareTeamDraft, b: CareTeamDraft): boolean =>
  normalizeProgram(a.program) === normalizeProgram(b.program) &&
  membersEqual(a.members, b.members);

/**
 * Edit dialog for the care-team panel (PTV2-1095).
 *
 * Working-copy model: the editor seeds a LOCAL copy of the team members +
 * metadata once when it opens, and every edit (add / remove / role / promote /
 * clear-primary / program / referral) mutates only that local copy. Nothing
 * touches the server until the footer's Save button, which diffs the working
 * copy against the original snapshot and commits the minimal set of per-action
 * mutations. Cancel / dismiss simply drops the working copy.
 */
export function CareTeamEditor({
  guid,
  members,
  resolveName,
  initialProgram,
  onClose,
}: CareTeamEditorProps): ReactElement {
  const { t } = useTranslation();

  // Snapshot the original members once so the dirty-diff stays stable even if
  // the underlying query refetches (e.g. background invalidation) mid-edit.
  const [originalMembers] = useState<readonly CareTeamMemberDetailViewModel[]>(
    () => members,
  );
  const [errorKind, setErrorKind] = useState<'forbidden' | 'generic' | null>(null);
  const [addResetKey, setAddResetKey] = useState(0);
  const [saving, setSaving] = useState(false);

  const addMember = useAddCareTeamMember(guid);
  const removeMember = useRemoveCareTeamMember(guid);
  const updateRole = useUpdateCareTeamMemberRole(guid);
  const setPrimary = useSetPrimaryClinician(guid);

  const metadataMutation = useMutation({
    mutationFn: async (input: { readonly program: string | null }) =>
      // Partial PATCH — only the keys we send are written, so omitting
      // `referral_source` leaves any existing value untouched (PTV2-1251
      // removed the referral field from this dialog).
      apiClient.updatePatientMetadata(guid, { program: input.program }),
  });

  const captureError = (err: unknown): void => {
    setErrorKind(isForbidden(err) ? 'forbidden' : 'generic');
  };

  // Diff the working copy against the original snapshot and commit the minimal
  // set of per-action mutations. Commit ONLY — never closes.
  const commitDraft = async (d: CareTeamDraft): Promise<void> => {
    const originalByUser = new Map(
      originalMembers.map((m) => [m.userId, m] as const),
    );
    const workingByUser = new Map(
      d.members.map((m) => [m.userId, m] as const),
    );

    // A member added then removed before Save nets to nothing: it is absent from
    // both `toAdd` (not new to the server it never reached) and `toRemove`
    // (never in the original), because both diffs key off `userId`.
    const toAdd = d.members.filter((m) => !originalByUser.has(m.userId));
    const toRemove = originalMembers.filter((m) => !workingByUser.has(m.userId));
    const toUpdateRole = d.members.filter((m) => {
      const orig = originalByUser.get(m.userId);
      return orig !== undefined && orig.role !== m.role;
    });

    const originalPrimaryUserId =
      originalMembers.find((m) => m.isPrimary)?.userId ?? null;
    const workingPrimaryUserId =
      d.members.find((m) => m.isPrimary)?.userId ?? null;
    const primaryChanged = originalPrimaryUserId !== workingPrimaryUserId;

    // 1. Adds first — a member must exist server-side before it can be made
    //    primary (step 3).
    await Promise.all(
      toAdd.map((m) =>
        addMember.mutateAsync({
          userId: m.userId,
          displayName: m.displayName ?? '',
          role: m.role,
        }),
      ),
    );

    // 2. Role changes on members that already existed on the server.
    await Promise.all(
      toUpdateRole.map((m) =>
        updateRole.mutateAsync({ memberId: m.id, role: m.role }),
      ),
    );

    // 3. Primary change (set or clear). Runs after adds so a freshly-added
    //    primary exists, and before removes so we never orphan the pointer.
    if (primaryChanged) {
      await setPrimary.mutateAsync({ userId: workingPrimaryUserId });
    }

    // 4. Removes last.
    await Promise.all(
      toRemove.map((m) => removeMember.mutateAsync({ memberId: m.id })),
    );

    // 5. Metadata piggy-backs on the legacy PATCH /metadata round-trip.
    const nextProgram = normalizeProgram(d.program);
    if (nextProgram !== normalizeProgram(initialProgram ?? '')) {
      await metadataMutation.mutateAsync({ program: nextProgram });
    }
  };

  // PTV2-1327: one primitive owns the working copy. Autosave is off — the
  // care-team diff is committed only by the explicit Save; the guard still
  // catches a dirty dismiss / refresh.
  const form = useEditableDraft<CareTeamDraft>({
    initial: { members: [...members], program: initialProgram ?? '' },
    label: t('patient.careTeam.editTitle'),
    autosave: false,
    isEqual: draftsEqual,
    onSave: (d) => commitDraft(d),
  });
  const { draft } = form;

  // PRD: the Primary is NOT in the team list — they're a structurally separate
  // slot. `supporting` is the non-primary tail of the working copy.
  const primary = draft.members.find((m) => m.isPrimary) ?? null;
  const supporting = draft.members.filter((m) => !m.isPrimary);

  const addToWorkingTeam = (input: {
    readonly userId: string;
    readonly displayName: string;
    readonly role: string;
  }): void => {
    setErrorKind(null);
    form.setDraft((d) => ({
      ...d,
      members: [
        ...d.members,
        {
          id: `${NEW_MEMBER_ID_PREFIX}${input.userId}`,
          userId: input.userId,
          displayName: input.displayName,
          role: input.role,
          isPrimary: false,
          addedAt: null,
          addedBy: null,
        },
      ],
    }));
    // Clear the picker + role field for the next add.
    setAddResetKey((k) => k + 1);
  };

  const removeFromWorkingTeam = (memberId: string): void => {
    setErrorKind(null);
    form.setDraft((d) => ({
      ...d,
      members: d.members.filter((m) => m.id !== memberId),
    }));
  };

  const changeWorkingRole = (memberId: string, role: string): void => {
    setErrorKind(null);
    form.setDraft((d) => ({
      ...d,
      members: d.members.map((m) => (m.id === memberId ? { ...m, role } : m)),
    }));
  };

  const promoteInWorkingTeam = (userId: string): void => {
    setErrorKind(null);
    form.setDraft((d) => ({
      ...d,
      members: d.members.map((m) => ({ ...m, isPrimary: m.userId === userId })),
    }));
  };

  const clearWorkingPrimary = (): void => {
    setErrorKind(null);
    form.setDraft((d) => ({
      ...d,
      members: d.members.map((m) => (m.isPrimary ? { ...m, isPrimary: false } : m)),
    }));
  };

  // Explicit submit: commit AND close.
  const submitAndClose = async (): Promise<void> => {
    if (saving) return;
    setErrorKind(null);
    setSaving(true);
    try {
      await commitDraft(draft);
      onClose();
    } catch (err) {
      captureError(err);
    } finally {
      setSaving(false);
    }
  };

  // PTV2-1301: guard dismiss / refresh while there are unsaved care-team changes.
  // `register: false` — the primitive already registers while dirty.
  const guard = useDirtyDialogGuard({
    dirty: form.isDirty,
    canSave: form.canSave,
    saving,
    label: t('patient.careTeam.editTitle'),
    onSave: () => {
      void submitAndClose();
    },
    onClose,
    register: false,
  });

  return (
    <>
    <Dialog open {...guard.rootProps}>
      <DialogContent className="max-w-md gap-4" {...guard.contentProps}>
        <DialogHeader>
          <DialogTitle>{t('patient.careTeam.editTitle')}</DialogTitle>
          <DialogDescription>{t('patient.careTeam.editDescription')}</DialogDescription>
        </DialogHeader>

        <div ref={form.containerProps.ref} className="space-y-4">
          <MetadataFields
            program={draft.program}
            onProgramChange={(next) => {
              form.setDraft((d) => ({ ...d, program: next }));
            }}
          />

          <PrimarySection
            label={t('patient.careTeam.primaryGc')}
            primary={primary === null ? null : resolveName(primary)}
            emptyLabel={t('patient.careTeam.primaryNone')}
            onClear={primary === null ? null : clearWorkingPrimary}
          />

          <Field htmlFor="" label={t('patient.careTeam.teamMembers')}>
            <MemberList
              members={supporting}
              resolveName={resolveName}
              onPromote={promoteInWorkingTeam}
              onRemove={removeFromWorkingTeam}
              onRoleChange={changeWorkingRole}
              busy={saving}
            />

            <AddMemberForm
              busy={saving}
              isAlreadyOnTeam={(userGuid) =>
                draft.members.some((m) => m.userId === userGuid)
              }
              onAdd={addToWorkingTeam}
              resetKey={addResetKey}
            />
          </Field>

          <EditorFooter
            errorKind={errorKind}
            saving={saving}
            busy={saving}
            dirty={form.isDirty}
            onCancel={guard.requestClose}
            onSave={() => {
              void submitAndClose();
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
    {guard.confirm}
    </>
  );
}
